<template>
    <MemberTabs class="mt-12" tab-key="invitations">
        <div class="mt-12 pb-80">
            <DataTable
                v-if="isAdmin || isOwner"
                :headers="headersInvitations"
                :items="invitations"
                searchable
                :title="$t('vue.team_invitations')"
            >
                <template #item-token="{ data }">
                    <div v-if="data" class="px-6 text-gray-500">
                        {{ teamProps.name }}
                    </div>
                </template>

                <template #item-created_at="{ data, row }">
                    <div class="px-6">
                        <div
                            class="inline-block rounded-md bg-orange-200 px-2 py-[3px] text-xs font-medium text-gray-700"
                        >
                            {{ $t("vue.invitation_pending") }} ({{
                                differenceInCalendarDays(
                                    new Date(),
                                    new Date(data as string),
                                )
                            }}
                            <span
                                v-html="
                                    differenceInCalendarDays(
                                        new Date(),
                                        new Date(data as string),
                                    ) !== 1
                                        ? $t('vue.days') + ')'
                                        : $t('vue.day') + ')'
                                "
                            ></span>

                            <span
                                class="ml-[10px] cursor-pointer text-custom-blue-500 hover:text-custom-blue-600"
                                @click="remindInvitation(row as IInvitation)"
                                >{{ $t("vue.remind") }}</span
                            >
                        </div>
                    </div>
                </template>
                <template #item-role="{ data }">
                    <div class="px-6">
                        <div
                            class="inline-block rounded-md bg-blue-200 px-2 py-[3px] text-sm"
                        >
                            {{ data }}
                        </div>
                    </div>
                </template>
                <template #item-custom="{ row }">
                    <AtomicButton
                        class="mx-4"
                        size="sm"
                        color="white"
                        @click="deleteInvitation(row as IInvitation)"
                    >
                        <template #icon>
                            <TrashIcon class="h-5 w-5 text-gray-400" />
                        </template>
                    </AtomicButton>
                </template>
            </DataTable>
        </div>
    </MemberTabs>
</template>

<script lang="ts" setup>
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import type IInvitation from "@/types/Members/IInvitation";
import type IDataTableHeader from "@/components/Atomic/DataTable/types/IDataTableHeader";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { TrashIcon } from "@heroicons/vue/24/outline";
import usePermissions from "@/composables/usePermissions";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { usePage } from "@inertiajs/vue3";
import { differenceInCalendarDays } from "date-fns";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { useTranslations } from "@/composables/useTranslations";

defineProps<{
    invitations: IPaginationData<IInvitation>;
}>();

// Get access to translations
const { t } = useTranslations();

const { isAdmin, isOwner } = usePermissions();

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

const headersInvitations: IDataTableHeader[] = [
    { text: t("vue.email"), id: "email" },
    { text: t("vue.team"), id: "token" },
    {
        text: t("vue.invitation_pending"),
        id: "created_at",
    },
    {
        text: t("vue.permissions_role"),
        id: "role",
        align: "left",
    },
    { text: "", id: "custom" },
];

const remindInvitation = (value: IInvitation) => {
    const form = {
        email: value.email,
        role: value.role,
    };
    router.post(route("members.store"), form, {
        preserveScroll: true,
        onSuccess: () => {
            create({
                title: t("vue.invitation_reminded"),
                type: "info",
            });
        },
        onError: () => {
            create({
                title: t("vue.invitation_not_reminded"),
                text: t("vue.error_reminding_invitation"),
                type: "error",
            });
        },
    });
};

const deleteInvitation = (value: IInvitation) => {
    router.get(
        route("team.invitation.refuse", { token: value.token }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.invitation_deleted"),
                    type: "info",
                });
            },
            onError: () => {
                create({
                    title: t("vue.invitation_not_deleted"),
                    text: t("vue.error_deleting_invitation"),
                    type: "error",
                });
            },
        },
    );
};
</script>
